import { createContext, ReactNode, useRef, useState } from 'react';
import { useTheme } from 'styled-components/macro';
import { useStore } from 'hooks';

import { TourGlobalStyles } from './tour.global-styled';
import { createTourPersistence, TourPersistence } from './tour.persistence';
import { TOUR_REGISTRY, TourRegistryKey } from './tour.registry';
import { TourRouteSync } from './tour.route-sync';
import { runTour } from './tour.runner';

type TourContextType = {
  startTour: (tourId: TourRegistryKey) => void;
  persistence: TourPersistence;
  activeTourId: TourRegistryKey | null;
};

export const TourContext = createContext<TourContextType | undefined>(
  undefined
);
const persistence = createTourPersistence(TOUR_REGISTRY);

export const TourProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const { onboardings, user } = useStore();

  const currentTourRef =
    useRef<Awaited<ReturnType<typeof runTour> | null>>(null);
  const [activeTourId, setActiveTourId] = useState<TourRegistryKey | null>(
    null
  );

  const startTour = async (id: TourRegistryKey) => {
    const tour = TOUR_REGISTRY[id];

    if (!tour) {
      return;
    }

    currentTourRef.current?.destroy?.();
    setActiveTourId(id);
    currentTourRef.current = await runTour(
      tour,
      theme,
      () => {
        onboardings.patchOnboardingInfo(
          {
            block: id,
            viewed: true,
            version: tour.version,
          },
          user.id
        );
      },
      () => setActiveTourId(null)
    );
  };

  return (
    <TourContext.Provider value={{ startTour, persistence, activeTourId }}>
      <TourGlobalStyles />
      <TourRouteSync currentTourRef={currentTourRef} />

      {children}
    </TourContext.Provider>
  );
};
